import React, { useEffect } from "react";
import _ from "@lodash";
import { Card, TextField, Typography, Divider } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { authDataService } from "src/app/services/data/auth.data.service";
import { UserRole } from "src/app/@types/enums/user.roles";

const schema = yup.object().shape({
  a: yup.number().nullable(),
  b: yup.number().nullable(),
  c: yup.number().nullable(),
  d: yup.number().nullable(),
  e: yup.number().nullable(),
  f: yup.number().nullable(),
  g: yup.number().nullable(),
  h: yup.number().nullable(),
  i: yup.number().nullable(),
  j: yup.number().nullable(),
  k: yup.number().nullable(),
  l: yup.number().nullable(),
  m: yup.number().nullable(),
  n: yup.number().nullable(),
  o: yup.number().nullable(),
});

const defaultValues = {
  a: "",
  b: "",
  c: "",
  d: "",
  e: "",
  f: "",
  g: "",
  h: "",
  i: "",
  j: "",
  k: "",
  l: "",
  m: "",
  n: "",
  o: "",
};

const W4AllowanceWorksheet = ({ isReadOnly = false }) => {
  const { t } = useTranslation("w4AllowanceCertificate");
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const form = watch();

  if (_.isEmpty(form)) return null;

  const sectionAFields = ["a", "b", "c", "d", "e", "f", "g", "h", "i"];
  const sectionBFields = ["j", "k", "l", "m", "n", "o"];

  useEffect(() => {
    if (authDataService.userRoleId == UserRole.Parent) {
      initData();
    }
  }, []);

  const initData = () => {
    const fieldValues = {
      a: 1,
      b: 0,
      c: 0,
      d: 0,
      e: 1,
      f: 0,
      g: 0,
      h: 0,
      i: 2,
      j: 0,
      k: 4000,
      l: -4000,
      m: 3350,
      n: 0,
      o: 2,
    };

    Object.entries(fieldValues).forEach(([field, value]) => {
      setValue(field as keyof typeof defaultValues, value.toString());
    });
  };

  type FieldName = keyof typeof defaultValues;

  const renderField = (
    name: FieldName,
    labelKey: string,
    fullWidth = false
  ) => (
    <div className="flex content-between justify-start m-8 " key={name}>
      <div className="flex w-4/5 mt-16 ">
        <Typography>{t(labelKey)}</Typography>
      </div>
      <div>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              disabled={isReadOnly}
              className={`ml-16 ${fullWidth ? "w-1/2" : "w-1/3"} text-center`}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors[name]}
              helperText={t(errors?.[name]?.message ?? "")}
            />
          )}
        />
      </div>
    </div>
  );

  return (
    <Card variant="outlined" className="m-24">
      <div className="flex content-between justify-start m-8">
        <div className="w-1/3 mt-16">
          <img
            className="h-52"
            src="assets/images/logo/logo-ypms-full.png"
            alt="logo"
          />
        </div>
        <div className="flex w-1/2 mt-16">
          <Typography className="font-800 h-32 mt-8 ml-4">
            {t("HEADER")}
          </Typography>
        </div>
      </div>

      <Divider />
      <div className="flex content-between justify-start m-8">
        <Typography className="font-600">{t("SECA_TITLE")}</Typography>
      </div>
      <Divider className="border-b-3 border-black font-bold h-4" />

      {sectionAFields.map((field) => (
        <React.Fragment key={field}>
          {renderField(field as FieldName, field.toUpperCase())}
          <Divider />
        </React.Fragment>
      ))}

      <div className="flex content-between justify-start m-8">
        <Typography className="font-600">{t("SECB_TITLE")}</Typography>
      </div>
      <Divider className="border-b-3 border-black font-bold h-4" />

      {sectionBFields.map((field) => (
        <React.Fragment key={field}>
          {renderField(
            field as FieldName,
            field.toUpperCase(),
            ["j", "k", "l", "m"].includes(field)
          )}
          <Divider />
        </React.Fragment>
      ))}
    </Card>
  );
};

export default W4AllowanceWorksheet;
